(() => {
  'use strict';
  if (window.MembracleanCart) return;

  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const drawer = () => document.querySelector('[data-mc-cart-drawer]');
  const panel = () => document.querySelector('[data-mc-cart-drawer-panel]');
  const cartUrl = () => drawer()?.dataset.cartUrl || rootUrl() + 'cart';
  const onCartPage = () => Boolean(document.querySelector('[data-mc-cart-page-content]')) ||
    location.pathname.replace(/\/$/, '') === new URL(cartUrl(), location.origin).pathname.replace(/\/$/, '');
  const drawerEnabled = () => Boolean(drawer()) && drawer().dataset.cartMode === 'drawer';
  const errorMessage = () => drawer()?.dataset.updateError || '';
  let busy = false;
  let lastFocus = null;
  let renderVersion = 0;
  let mutationFocus = null;
  let backgroundState = null;
  const disabledControls = new Map();
  const productFormSelector = '[data-mc-product-form], .mc-product-inline-upsell__form, .mc-gutschein-pro__app form';
  const controlSelector = '[data-mc-cart-qty-minus], [data-mc-cart-qty-plus], [data-mc-cart-remove], [data-mc-cart-qty-input], [data-mc-cart-upsell-add], [data-mc-product-submit], .mc-product-inline-upsell__cart-button, [data-mc-cart-checkout-form] [name="checkout"]';

  const setBusy = (state) => {
    busy = state;
    document.documentElement.classList.toggle('mc-cart-busy', state);
    document.querySelectorAll('[data-mc-cart-content], ' + productFormSelector).forEach((el) => {
      if (state) el.setAttribute('aria-busy', 'true');
      else el.removeAttribute('aria-busy');
    });
    if (state) {
      document.querySelectorAll(controlSelector).forEach((el) => {
        disabledControls.set(el, el.disabled);
        el.disabled = true;
      });
    } else {
      disabledControls.forEach((wasDisabled, el) => {
        if (el.isConnected) {
          const variant = el.matches('[data-mc-product-submit], .mc-product-inline-upsell__cart-button') ? el.form?.querySelector('[name="id"]') : null;
          el.disabled = wasDisabled || Boolean(variant?.disabled);
        }
      });
      disabledControls.clear();
    }
  };

  const focusCartPage = () => {
    const heading = document.querySelector('[data-mc-cart-page-title]');
    heading?.focus({ preventScroll: true });
    heading?.scrollIntoView({ block: 'start' });
  };

  const openDrawer = (trigger = document.activeElement) => {
    if (onCartPage()) { focusCartPage(); return; }
    if (!drawerEnabled()) return;
    const el = drawer();
    if (!el.hidden) return;
    lastFocus = trigger;
    el.hidden = false;
    document.documentElement.classList.add('mc-cart-open');
    const background = document.getElementById('PageContainer');
    if (background) {
      backgroundState = { el: background, inert: background.inert };
      background.inert = true;
    }
    document.querySelectorAll('[data-mc-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    window.requestAnimationFrame(() => panel()?.focus({ preventScroll: true }));
  };

  const closeDrawer = (restoreFocus = true) => {
    const el = drawer();
    if (!el) return;
    el.hidden = true;
    document.documentElement.classList.remove('mc-cart-open');
    if (backgroundState) {
      backgroundState.el.inert = backgroundState.inert;
      backgroundState = null;
    }
    document.querySelectorAll('[data-mc-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    if (restoreFocus && lastFocus?.isConnected && !lastFocus.disabled) lastFocus.focus({ preventScroll: true });
    lastFocus = null;
  };

  const showError = (message, form = null) => {
    const scope = form || ((!drawer()?.hidden && panel()) || document.querySelector('[data-mc-cart-page-content]') || drawer());
    let target = scope?.querySelector('[data-mc-cart-error], [data-mc-product-error]');
    if (!target && form) {
      target = document.createElement('p');
      target.className = 'mc-cart-error';
      target.dataset.mcProductError = '';
      target.setAttribute('role', 'alert');
      form.appendChild(target);
    }
    if (target) { target.textContent = message || errorMessage(); target.hidden = false; }
  };

  const clearErrors = () => document.querySelectorAll('[data-mc-cart-error], [data-mc-product-error]').forEach((el) => {
    el.textContent = '';
    el.hidden = true;
  });

  const focusSnapshot = () => {
    const el = document.activeElement;
    const item = el?.closest('[data-mc-cart-item]');
    if (!item) return null;
    const attr = ['data-mc-cart-qty-minus', 'data-mc-cart-qty-plus', 'data-mc-cart-remove', 'data-mc-cart-qty-input'].find((name) => el.hasAttribute(name));
    return attr ? { key: item.dataset.lineKey, attr, context: el.closest('[data-mc-cart-content]')?.dataset.mcCartContent } : null;
  };

  const restoreCartFocus = (snapshot) => {
    if (!snapshot) return;
    const scope = document.querySelector('[data-mc-cart-content="' + snapshot.context + '"]');
    const item = Array.from(scope?.querySelectorAll('[data-mc-cart-item]') || []).find((el) => el.dataset.lineKey === snapshot.key);
    const target = item?.querySelector('[' + snapshot.attr + ']') || scope?.querySelector('[data-mc-cart-qty-input], [data-mc-cart-close], a');
    if (target && !target.disabled && target.getClientRects().length) target.focus({ preventScroll: true });
  };

  const initUpsells = (scope = document) => {
    scope.querySelectorAll('[data-mc-cart-upsell]').forEach((root) => {
      if (root.dataset.initialized === 'true') return;
      root.dataset.initialized = 'true';

      const allCandidates = Array.from(root.querySelectorAll('.mc-cart-upsell__candidate'));
      const seen = new Set();
      const primaryCandidates = [];
      const fallbackCandidates = [];

      allCandidates.forEach((candidate) => {
        const productId = candidate.dataset.productId;
        if (!productId || seen.has(productId) || candidate.dataset.inCart === 'true') {
          candidate.remove();
          return;
        }
        seen.add(productId);
        if (candidate.dataset.fallback === 'true') {
          fallbackCandidates.push(candidate);
        } else {
          primaryCandidates.push(candidate);
        }
      });

      const shuffle = (items) => {
        for (let index = items.length - 1; index > 0; index -= 1) {
          const randomIndex = Math.floor(Math.random() * (index + 1));
          [items[index], items[randomIndex]] = [items[randomIndex], items[index]];
        }
      };

      if (root.dataset.randomize === 'true') {
        shuffle(primaryCandidates);
        shuffle(fallbackCandidates);
      }

      const candidates = [...primaryCandidates, ...fallbackCandidates];
      const grid = root.querySelector('.mc-cart-upsell__grid');
      const requested = Number.parseInt(root.dataset.showCount || '4', 10);
      const maxForContext = root.dataset.location === 'drawer' ? 3 : 6;
      const requestedWithMinimum = Math.max(requested, 3);
      const showCount = Math.min(requestedWithMinimum, maxForContext, candidates.length);

      candidates.forEach((candidate, index) => {
        if (index < showCount) {
          candidate.hidden = false;
          grid?.appendChild(candidate);
        } else {
          candidate.remove();
        }
      });

      root.hidden = showCount === 0;
    });
  };

  const applyCartHTML = (html, version) => {
    if (version !== renderVersion) return null;
    if (typeof html !== 'string' || /Liquid (?:error|syntax error)/i.test(html)) throw new Error(errorMessage());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const pageSource = doc.querySelector('[data-mc-cart-ajax-page]');
    const drawerSource = doc.querySelector('[data-mc-cart-ajax-drawer]');
    const countSource = doc.querySelector('[data-mc-cart-ajax-count]');
    const cart = JSON.parse(doc.querySelector('[data-mc-cart-ajax-data]')?.textContent || 'null');
    if (!pageSource || !drawerSource || !countSource || !Number.isInteger(cart?.item_count)) throw new Error(errorMessage());
    const snapshot = focusSnapshot() || (document.activeElement === document.body ? mutationFocus : null);
    const consentState = new Map(Array.from(document.querySelectorAll('[data-mc-cart-content]')).map((el) => [el.dataset.mcCartContent, Boolean(el.querySelector('.mc-cart-consent__input')?.checked)]));
    const pageTarget = document.querySelector('[data-mc-cart-page-content]');
    const drawerTarget = document.querySelector('[data-mc-cart-drawer-content]');
    if (pageTarget) pageTarget.innerHTML = pageSource.innerHTML;
    if (drawerTarget) drawerTarget.innerHTML = drawerSource.innerHTML;
    document.querySelectorAll('[data-mc-cart-content]').forEach((el) => {
      const checkbox = el.querySelector('.mc-cart-consent__input');
      if (checkbox) checkbox.checked = consentState.get(el.dataset.mcCartContent) === true;
    });
    document.querySelectorAll('[data-mc2-cart-count]').forEach((counter) => {
      counter.textContent = String(cart.item_count);
      counter.classList.toggle('is-empty', cart.item_count === 0);
    });
    const label = countSource.dataset.label || String(cart.item_count);
    document.querySelectorAll('[data-mc-cart-drawer-count], [data-mc-cart-status]').forEach((el) => { el.textContent = label; });
    initUpsells(document);
    restoreCartFocus(snapshot);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { ...cart, cart, source: 'membraclean' } }));
    return cart;
  };

  const fetchCartHTML = async () => {
    const url = new URL(cartUrl(), location.origin);
    url.searchParams.set('section_id', 'mc-cart-ajax');
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(errorMessage());
    return response.text();
  };

  const refresh = async ({ open = false } = {}) => {
    if (busy) return null;
    const version = ++renderVersion;
    const cart = applyCartHTML(await fetchCartHTML(), version);
    if (open && cart) openDrawer();
    return cart;
  };

  const runMutation = async (endpoint, body, { form = null, open = false, trigger = document.activeElement } = {}) => {
    if (busy) return false;
    const version = ++renderVersion;
    clearErrors();
    mutationFocus = focusSnapshot();
    setBusy(true);
    let committed = false;
    try {
      const multipart = body instanceof FormData;
      const sectionsUrl = new URL(cartUrl(), location.origin).pathname;
      if (multipart) {
        body.set('sections', 'mc-cart-ajax');
        body.set('sections_url', sectionsUrl);
      } else {
        body = { ...body, sections: ['mc-cart-ajax'], sections_url: sectionsUrl };
      }
      const response = await fetch(rootUrl() + endpoint, {
        method: 'POST', credentials: 'same-origin',
        headers: multipart ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: multipart ? body : JSON.stringify(body)
      });
      committed = response.ok;
      const data = await response.json();
      if (!response.ok || data.status >= 400) {
        committed = false;
        throw new Error(typeof data.description === 'string' ? data.description : (typeof data.message === 'string' ? data.message : errorMessage()));
      }
      let cart;
      try { cart = applyCartHTML(data.sections?.['mc-cart-ajax'], version); }
      catch { cart = applyCartHTML(await fetchCartHTML(), version); }
      if (!cart) throw new Error(errorMessage());
      if (open) openDrawer(trigger);
      return true;
    } catch (error) {
      if (committed) {
        location.assign(cartUrl());
      } else {
        try { applyCartHTML(await fetchCartHTML(), version); } catch {}
        showError(error.message, form);
      }
      return false;
    } finally {
      setBusy(false);
      mutationFocus = null;
    }
  };

  const changeLine = (key, quantity) => {
    if (!key || !Number.isSafeInteger(quantity) || quantity < 0) { showError(errorMessage()); return; }
    return runMutation('cart/change.js', { id: key, quantity });
  };

  const addUpsellVariant = (variantId) => {
    const id = Number(variantId);
    if (!Number.isSafeInteger(id) || id <= 0) return;
    return runMutation('cart/add.js', { items: [{ id, quantity: 1 }] });
  };

  const requireConsent = (form, event) => {
    const checkbox = form.querySelector('.mc-cart-consent__input');
    const error = form.querySelector('[data-terms-error]');
    const consent = checkbox?.closest('.mc-cart-consent');
    if (!checkbox || checkbox.checked) {
      if (error) error.hidden = true;
      checkbox?.removeAttribute('aria-invalid');
      consent?.classList.remove('is-error');
      return true;
    }
    event.preventDefault();
    if (error) error.hidden = false;
    checkbox.setAttribute('aria-invalid', 'true');
    consent?.classList.add('is-error');
    checkbox.focus();
    return false;
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element) || !drawerEnabled()) return;

    const button = event.target.closest('.mc-gutschein-pro__app button, .mc-gutschein-pro__app [role="button"]');
    if (!button) return;

    const label = (button.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const isAddToCart = label.includes('in den warenkorb') || label.includes('warenkorb');
    if (!isAddToCart) return;

    const app = button.closest('.mc-gutschein-pro__app');
    const form = button.form || app?.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (busy || button.disabled) return;
    if (!form.reportValidity()) return;

    const variant = form.querySelector('[name="id"]');
    if (!variant || variant.disabled || !variant.value) {
      showError(errorMessage(), form);
      return;
    }

    runMutation('cart/add.js', new FormData(form), {
      form,
      open: true,
      trigger: button
    });
  }, true);

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;
    const ordinaryClick = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    const cartTrigger = target.closest('[data-mc-cart-open], a.mc2-cart');
    if (cartTrigger && ordinaryClick && !event.defaultPrevented) {
      if (onCartPage()) { event.preventDefault(); focusCartPage(); return; }
      if (!drawerEnabled()) return;
      event.preventDefault();
      openDrawer(cartTrigger);
      if (!busy) refresh().catch(() => showError(errorMessage()));
      return;
    }
    if (target.closest('[data-mc-cart-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }
    const upsellAdd = target.closest('[data-mc-cart-upsell-add]');
    if (upsellAdd) { event.preventDefault(); addUpsellVariant(upsellAdd.dataset.variantId); return; }
    const remove = target.closest('[data-mc-cart-remove]');
    if (remove) { event.preventDefault(); changeLine(remove.dataset.lineKey, 0); return; }
    const quantityButton = target.closest('[data-mc-cart-qty-minus], [data-mc-cart-qty-plus]');
    if (!quantityButton) return;
    event.preventDefault();
    const input = quantityButton.closest('[data-mc-cart-item]')?.querySelector('[data-mc-cart-qty-input]');
    const value = Number(input?.value);
    const step = Number(input?.step) > 0 ? Number(input.step) : 1;
    const next = value + (quantityButton.hasAttribute('data-mc-cart-qty-minus') ? -step : step);
    changeLine(quantityButton.dataset.lineKey, Math.max(0, next));
  });

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element)) return;
    const input = event.target.closest('[data-mc-cart-qty-input]');
    if (input) {
      if (!input.value.trim() || !input.checkValidity()) { showError(errorMessage()); input.reportValidity(); return; }
      changeLine(input.dataset.lineKey, Number(input.value));
      return;
    }
    const checkbox = event.target.closest('.mc-cart-consent__input');
    if (checkbox?.checked) {
      const error = checkbox.closest('form')?.querySelector('[data-terms-error]');
      if (error) error.hidden = true;
      checkbox.removeAttribute('aria-invalid');
      checkbox.closest('.mc-cart-consent')?.classList.remove('is-error');
    }
  });

  document.addEventListener('submit', (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    const form = event.target;
    if (!form.matches('.mc-gutschein-pro__app form') || !drawerEnabled()) return;
    if (event.submitter?.name === 'checkout') return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (busy || event.submitter?.disabled) return;
    if (!form.reportValidity()) return;

    const variant = form.querySelector('[name="id"]');
    if (!variant || variant.disabled || !variant.value) {
      showError(errorMessage(), form);
      return;
    }

    runMutation('cart/add.js', new FormData(form), {
      form,
      open: true,
      trigger: event.submitter || document.activeElement
    });
  }, true);

  document.addEventListener('submit', (event) => {
    if (!(event.target instanceof HTMLFormElement) || event.defaultPrevented) return;
    const form = event.target;
    if (form.matches('[data-mc-cart-checkout-form]')) {
      if (busy) { event.preventDefault(); return; }
      requireConsent(form, event);
      return;
    }
    if (!form.matches(productFormSelector) || !drawerEnabled()) return;
    if (event.submitter?.name === 'checkout') return;
    event.preventDefault();
    if (busy || event.submitter?.disabled) return;
    if (!form.reportValidity()) return;
    const variant = form.querySelector('[name="id"]');
    if (!variant || variant.disabled || !variant.value) { showError(errorMessage(), form); return; }
    runMutation('cart/add.js', new FormData(form), { form, open: true, trigger: event.submitter || document.activeElement });
  });

  document.addEventListener('keydown', (event) => {
    if (drawer()?.hidden !== false) return;
    if (event.key === 'Escape') { event.preventDefault(); closeDrawer(); return; }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(panel()?.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') || [])
      .filter((el) => !el.hidden && el.getClientRects().length > 0);
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (!first) { event.preventDefault(); panel()?.focus(); return; }
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel())) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  const requestRefresh = (open) => refresh({ open }).catch(() => showError(errorMessage()));
  document.addEventListener('ajaxProduct:added', () => requestRefresh(true));
  document.addEventListener('cart:build', () => requestRefresh(true));
  document.addEventListener('mc:cart:refresh', (event) => requestRefresh(Boolean(event.detail?.open)));
  document.addEventListener('shopify:section:load', () => initUpsells(document));
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    closeDrawer(false);
    requestRefresh(false);
  });

  window.MembracleanCart = { refresh, open: openDrawer, close: closeDrawer };
  initUpsells(document);
})();
